import React, { FC, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { GetGoogleLinkedAccountsResponse } from '@dreamai/types';
import api from '../../api';
import DeleteAccountSection from './DeleteAccountSection';
import './your-details-section.scss';
import './your-account-section.scss';

const LockIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' aria-hidden>
    <path
      d='M17 10V8C17 5.23858 14.7614 3 12 3C9.23858 3 7 5.23858 7 8V10M12 14.5V16.5M8.8 21H15.2C16.8802 21 17.7202 21 18.362 20.673C18.9265 20.3854 19.3854 19.9265 19.673 19.362C20 18.7202 20 17.8802 20 16.2V14.8C20 13.1198 20 12.2798 19.673 11.638C19.3854 11.0735 18.9265 10.6146 18.362 10.327C17.7202 10 16.8802 10 15.2 10H8.8C7.11984 10 6.27976 10 5.63803 10.327C5.07354 10.6146 4.6146 11.0735 4.32698 11.638C4 12.2798 4 13.1198 4 14.8V16.2C4 17.8802 4 18.7202 4.32698 19.362C4.6146 19.9265 5.07354 20.3854 5.63803 20.673C6.27976 21 7.11984 21 8.8 21Z'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const useHasLinkedSocialLogin = (): boolean => {
  const { getAccessTokenSilently } = useAuth0();
  const [hasLinked, setHasLinked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };
        const [google, microsoft] = await Promise.all([
          api.get<GetGoogleLinkedAccountsResponse>('/google-link', { headers }),
          api.get<{ linkedAccounts: { id: number }[] }>('/microsoft-link', {
            headers,
          }),
        ]);
        if (!cancelled) {
          setHasLinked(
            google.data.linkedAccounts.length > 0 ||
              microsoft.data.linkedAccounts.length > 0,
          );
        }
      } catch (err) {
        console.error('Error fetching linked login accounts:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently]);

  return hasLinked;
};

const SecuritySection: FC = () => {
  const hasLinkedSocialLogin = useHasLinkedSocialLogin();

  return (
    <div className='your-details-section your-account-section'>
      <div className='your-details-header'>
        <div className='your-details-heading'>
          <h2 className='section-title'>Your account</h2>
          <p className='your-details-subtitle'>Manage your Dream account.</p>
        </div>
      </div>

      <div className='your-account-signin-note' role='note'>
        <span className='your-account-signin-icon'>
          <LockIcon />
        </span>
        <div className='your-account-signin-text'>
          <p className='your-account-signin-title'>
            You&apos;re set up with passwordless login
          </p>
          <p className='your-account-signin-body'>
            No passwords to remember. When you log in we&apos;ll text a code to
            the phone number connected to your WhatsApp.
            {hasLinkedSocialLogin &&
              ' You can also log in using the Google or Outlook accounts you\u2019ve connected.'}
          </p>
        </div>
      </div>

      <DeleteAccountSection />
    </div>
  );
};

export default SecuritySection;
